import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";
import LocalPharmacyIcon from "@material-ui/icons/LocalPharmacy";
import CameraIcon from "@material-ui/icons/Camera";
import OpacityIcon from "@material-ui/icons/Opacity";
import PhoneIcon from "@material-ui/icons/Phone";
import EmailIcon from "@material-ui/icons/Email";
import PlaceIcon from "@material-ui/icons/Place";
import {
  getNameById,
  getPharmacyTypeLabel,
  PHARMACY_FIELD_LABELS,
  REQUIRED_PHARMACY_FIELDS,
} from "../../../utils/pharmacyValidation";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  preview: {
    position: "relative",
    overflow: "hidden",
    borderRadius: 16,
    padding: theme.spacing(2.5),
    color: "#eaf6ff",
    background: "linear-gradient(145deg, #085279 0%, #0a6e9c 55%, #0077B6 100%)",
    boxShadow: "0 20px 36px -22px rgba(8, 82, 121, .9)",
    "&::after": {
      content: '""',
      position: "absolute",
      width: 190,
      height: 190,
      right: -70,
      top: -80,
      borderRadius: "50%",
      background: "rgba(33, 205, 189, .22)",
    },
    "&::before": {
      content: '""',
      position: "absolute",
      width: 120,
      height: 120,
      right: 30,
      bottom: -70,
      borderRadius: "50%",
      background: "rgba(255, 255, 255, .07)",
    },
  },
  previewTop: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    marginBottom: theme.spacing(1.5),
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: "0.6875rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#04344b",
    background: "#21cdbd",
    whiteSpace: "nowrap",
  },
  badgeEmpty: {
    color: "#eaf6ff",
    background: "rgba(255,255,255,.18)",
  },
  previewName: {
    position: "relative",
    zIndex: 1,
    fontSize: "1.3125rem",
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: "-0.015em",
    color: "#fff",
    wordBreak: "break-word",
  },
  previewNameEmpty: {
    color: "rgba(234, 246, 255, .62)",
    fontStyle: "italic",
    fontWeight: 500,
  },
  previewLocation: {
    position: "relative",
    zIndex: 1,
    marginTop: theme.spacing(0.75),
    fontSize: "0.8125rem",
    color: "rgba(234, 246, 255, .82)",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  contactList: {
    position: "relative",
    zIndex: 1,
    listStyle: "none",
    margin: theme.spacing(1.5, 0, 0),
    padding: 0,
    display: "grid",
    gap: 6,
  },
  contactItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: "0.8125rem",
    color: "rgba(234, 246, 255, .9)",
  },
  contactEmpty: {
    color: "rgba(234, 246, 255, .45)",
  },
  panel: {
    border: "1px solid #e2eaf1",
    borderRadius: 16,
    background: "#fff",
    padding: theme.spacing(2.25),
  },
  panelTitle: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  panelHeading: {
    fontSize: "0.6875rem",
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "#6d8496",
  },
  counter: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#0f3a52",
    fontVariantNumeric: "tabular-nums",
  },
  progress: {
    height: 6,
    borderRadius: 999,
    background: "#eaf0f5",
    overflow: "hidden",
    marginBottom: theme.spacing(1.75),
  },
  progressBar: {
    height: "100%",
    borderRadius: 999,
    background: "linear-gradient(90deg, #21cdbd 0%, #0077B6 100%)",
    transition: "width 320ms cubic-bezier(.22,.61,.36,1)",
  },
  checklist: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "grid",
    gap: 10,
  },
  checklistItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontSize: "0.8125rem",
    color: "#42596b",
  },
  checklistDone: {
    color: "#0f3a52",
  },
  iconDone: {
    color: "#21a08f",
    fontSize: "1.05rem !important",
  },
  iconPending: {
    color: "#c3d0da",
    fontSize: "1.05rem !important",
  },
  hint: {
    marginTop: theme.spacing(1.75),
    padding: theme.spacing(1.25),
    borderRadius: 12,
    background: "#f4f8fb",
    fontSize: "0.75rem",
    lineHeight: 1.5,
    color: "#5d7385",
  },
}));

const TYPE_ICONS = {
  phar: LocalPharmacyIcon,
  img: CameraIcon,
  lab: OpacityIcon,
};

/**
 * Live preview of the place plus a checklist of the fields that are still
 * missing, so the admin always knows what is left before saving.
 */
export default function PlaceSummary({
  values,
  countries,
  cities,
  completion,
  t,
  showErrors,
}) {
  const classes = useStyles();

  const cityName = getNameById(cities, values.city_id);
  const countryName = getNameById(countries, values.country_id);
  const TypeIcon = TYPE_ICONS[values.type];
  const hasType = !!TypeIcon;

  return (
    <div className={classes.root}>
      <div className={classes.preview}>
        <div className={classes.previewTop}>
          <span
            className={`${classes.badge} ${hasType ? "" : classes.badgeEmpty}`}
          >
            {hasType && <TypeIcon style={{ fontSize: "0.9rem" }} />}
            {values.type ? getPharmacyTypeLabel(values.type, t) : t("Type")}
          </span>
        </div>

        <div
          className={`${classes.previewName} ${
            values.name ? "" : classes.previewNameEmpty
          }`}
        >
          {values.name || t("Name of the place")}
        </div>

        <div className={classes.previewLocation}>
          <PlaceIcon style={{ fontSize: "0.95rem" }} />
          {cityName || countryName
            ? `${cityName || t("City")}${countryName ? ` · ${countryName}` : ""}`
            : t("Country and city")}
        </div>

        <ul className={classes.contactList}>
          <li className={classes.contactItem}>
            <PhoneIcon style={{ fontSize: "0.95rem" }} />
            {values.phone ? (
              values.phone
            ) : (
              <span className={classes.contactEmpty}>{t("Phone number")}</span>
            )}
          </li>
          <li className={classes.contactItem}>
            <EmailIcon style={{ fontSize: "0.95rem" }} />
            {values.email ? (
              values.email
            ) : (
              <span className={classes.contactEmpty}>E-mail</span>
            )}
          </li>
        </ul>
      </div>

      <div className={classes.panel}>
        <div className={classes.panelTitle}>
          <span className={classes.panelHeading}>{t("Required fields")}</span>
          <span className={classes.counter}>
            {completion.completed}/{completion.total}
          </span>
        </div>

        <div className={classes.progress}>
          <div
            className={classes.progressBar}
            style={{ width: `${completion.percent}%` }}
          />
        </div>

        <ul className={classes.checklist}>
          {REQUIRED_PHARMACY_FIELDS.map((field) => {
            const done = !completion.missing.includes(field);
            return (
              <li
                key={field}
                className={`${classes.checklistItem} ${
                  done ? classes.checklistDone : ""
                }`}
              >
                {done ? (
                  <CheckCircleIcon className={classes.iconDone} />
                ) : (
                  <RadioButtonUncheckedIcon className={classes.iconPending} />
                )}
                {t(PHARMACY_FIELD_LABELS[field])}
              </li>
            );
          })}
        </ul>

        {showErrors && completion.missing.length > 0 && (
          <p className={classes.hint}>
            {t(
              "Complete the highlighted fields to register the place. The list marks what is still missing."
            )}
          </p>
        )}
      </div>
    </div>
  );
}
